import {ApiError} from '@legit.health/backend-client';
import {
  BodySite,
  FormActions,
  QuestionnaireAccordion,
  ScoringSystem,
  TextWithIcon,
  renderFacetQuestion
} from '@legit.health/ui';
import ListAltIcon from '@mui/icons-material/ListAlt';
import {Box, Typography} from '@mui/material';
import {TFunction} from 'i18next';
import {UseFormReturn} from 'react-hook-form';
import {FormValues} from './interfaces/FormValues';

export default function QuestionnairesFormView({
  apiError,
  isSuccess,
  isPending,
  bodySite,
  form,
  isForPatient = false,
  onSubmit,
  scoringSystems,
  showQuestionnairesHeader,
  t
}: {
  apiError: ApiError | null;
  isSuccess: boolean;
  isPending: boolean;
  bodySite: BodySite;
  form: UseFormReturn<FormValues>;
  isForPatient?: boolean;
  onSubmit: (values: FormValues) => void;
  scoringSystems: ScoringSystem[];
  showQuestionnairesHeader: boolean;
  t: TFunction;
}) {
  return (
    <Box data-testid="medicalQuestionnaireContainer">
      {showQuestionnairesHeader && (
        <Typography variant="h1" sx={{mb: 2}}>
          {isForPatient
            ? t('app:medicalQuestionnaire.labelForPatient')
            : t('app:medicalQuestionnaire.label')}
        </Typography>
      )}
      <Box sx={{mt: {xs: 1, md: 3}, mb: {xs: 1, md: 3}}}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Box mb={4}>
            {scoringSystems.map((scoringSystem, i) => (
              <div key={scoringSystem.id}>
                <QuestionnaireAccordion
                  scoringSystem={scoringSystem}
                  form={form}
                  renderFacetLabel={
                    isForPatient
                      ? function (facet) {
                          const bodySiteGroup = scoringSystem.bodySiteGroups?.[bodySite.code];
                          return renderFacetQuestion({
                            bodySiteName: bodySite.name,
                            bodySiteGroup: bodySiteGroup
                              ? t(`ui:bodySiteGroups.${bodySiteGroup}`)
                              : undefined,
                            facetQuestion: facet.question,
                            facetName: facet.name
                          });
                        }
                      : undefined
                  }
                  accordionSummary={
                    <TextWithIcon iconLeft={<ListAltIcon />} fontWeight="bold">
                      {isForPatient
                        ? t('app:questionnaire', {index: i + 1})
                        : scoringSystem.code.replaceAll('_', ' ')}
                    </TextWithIcon>
                  }
                />
              </div>
            ))}
            {scoringSystems.length === 0 && (
              <>
                <Typography gutterBottom fontWeight="bold">
                  {t('app:medicalQuestionnaire.noQuestions.title')}
                </Typography>
                <Typography>{t('app:medicalQuestionnaire.noQuestions.text')}</Typography>
              </>
            )}
          </Box>
          <FormActions
            error={apiError}
            isPending={isPending}
            isSuccess={isSuccess}
            actionButtonText={t('ui:send')}
            actionButtonTextSending={t('ui:sending')}
            buttonProps={{fullWidth: true}}
          />
        </form>
      </Box>
    </Box>
  );
}
